import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ReactDOM from "react-dom";
import { set_sync_status } from "../../actions/Sync";
import ExpenseMask from "../Slots/ExpenseMask";
import { getCalendarWeekForDate, getFriday, getMonday } from "../../libs/CalendarWeek";
import SyncConstants from "../../constants/SyncConstants";
import axios from "axios";
import useAuthentication from "../../hooks/useAuthentication";
import moment from "moment";
import ActivityFeed from "../Activity/ActivityFeed";
import WeeklyHoursWidget from "./WeeklyHoursWidget";
import AllUsersDailyStatsWidget from "./AllUsersDailyStatsWidget";
import MonthlyStatsWidget from "./MonthlyStatsWidget";
import "./Dashboard.scss";

const Dashboard = () => {
	const dispatch = useDispatch();
	const Auth = useAuthentication();

	const Team = useSelector((state) => state.Team);
	const UserSettings = useSelector((state) => state.User);
	const CurrentUser = useSelector((state) => state.CurrentUser);

	// States
	const [CurrentUserObject, setCurrentUserObject] = useState("");
	const [expenseEntryMask, setExpenseEntryMask] = useState(false);
	const [weeklyExpenses, setWeeklyExpenses] = useState([]);
	const [loadingStats, setLoadingStats] = useState(true);
	const [streak, setStreak] = useState(0);
	const [showGlobalStats, setShowGlobalStats] = useState(false);

	useEffect(() => {
		setCurrentUserObject(Team[UserSettings.userName]);
	}, [Team, UserSettings.userName]);

	useEffect(() => {
		dispatch(set_sync_status("asap_full"));
		loadDashboardStats();
	}, []);

	const loadDashboardStats = () => {
		const monday = getMonday(new Date());
		const friday = getFriday(new Date());

		if (Auth.getAuthObject()) {
			axios({
				method: "get",
				url: SyncConstants["syncBaseURL"] + "expenseEntries/myWeeklyStats",
				auth: Auth.getAuthObject(),
				params: {
					dateFrom: moment(monday).format("YYYY-MM-DD"),
					dateTo: moment(friday).format("YYYY-MM-DD")
				}
			}).then(response => {
				if (response.data.success) {
					setWeeklyExpenses(response.data.data || []);
					setStreak(response.data.streak || 0);
				}
				setLoadingStats(false);
			}).catch(() => setLoadingStats(false));
		}
	};

	const totalWeeklyMinutes = weeklyExpenses.reduce((sum, exp) => sum + (exp.total_minutes || 0), 0);

	const formatMinutes = (minutes) => {
		const hours = Math.floor(minutes / 60);
		const mins = minutes % 60;
		return `${hours}:${mins.toString().padStart(2, '0')} h`;
	};

	return (
		<>
			{/* Expense Entry Modal */}
			{expenseEntryMask && ReactDOM.createPortal(
				<div
					className="dashboard-modal-overlay overlay d-flex justify-content-center align-items-center"
					onClick={(e) => {
						if (e.target === e.currentTarget) {
							setExpenseEntryMask(false);
						}
					}}
				>
					<div className="modal d-block">
						<div className="modal-dialog dashboard-modal-dialog">
							<div className="modal-content">
								<div className="modal-header">
									<h5 className="modal-title">Aufwand buchen</h5>
									<button type="button" className="btn-close" onClick={() => setExpenseEntryMask(false)}></button>
								</div>
								<div className="modal-body">
									<ExpenseMask
										slotId=""
										afterSubmit={() => {
											setExpenseEntryMask(false);
											loadDashboardStats();
										}}
										hideDeleteButton={true}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>,
				document.body
			)}

			<div className="container mt-5">
				{/* Welcome Header */}
				<div className="mb-5">
					<div className="d-flex justify-content-between align-items-center">
						<div>
							{typeof CurrentUserObject != "undefined" ? (
								<h1>
									<i className="bi bi-house-door me-2 text-primary"></i>
									Hi, {CurrentUserObject.realName}!
								</h1>
							) : (
								<h1>
									<i className="bi bi-house-door me-2 text-primary"></i>
									Hi! <small className="text-muted">(Wir synchronisieren gerade noch etwas)</small>
								</h1>
							)}
							<p className="text-muted">Willkommen zurück bei Slotify</p>
						</div>
						
						{/* Stats Switcher - for all users */}
						{CurrentUser.data && (
							<div className="dashboard-stats-switcher d-flex align-items-center gap-3">
								<span className={`small ${!showGlobalStats ? 'fw-bold text-primary' : 'text-muted'}`}>
									Eigene Statistiken
								</span>
								<div className="form-check form-switch mb-0">
									<input
										className="form-check-input dashboard-stats-toggle"
										type="checkbox"
										role="switch"
										id="statsToggle"
										checked={showGlobalStats}
										onChange={(e) => setShowGlobalStats(e.target.checked)}
									/>
									<label className="form-check-label visually-hidden" htmlFor="statsToggle">
										Statistik-Modus
									</label>
								</div>
								<span className={`small ${showGlobalStats ? 'fw-bold text-primary' : 'text-muted'}`}>
									Globale Statistiken
								</span>
							</div>
						)}
					</div>
				</div>

				{/* Quick Actions */}
				<div className="row g-3 mb-4">
					<div className="col-md-4">
						<div className="card h-100 border-0 dashboard-widget-card">
							<div className="dashboard-widget-gradient dashboard-widget-gradient--primary"></div>
							<div className="card-body position-relative dashboard-widget-body">
								<h5 className="card-title mb-3 dashboard-widget-title">
									<i className="bi bi-plus-circle me-2 text-primary dashboard-widget-title-icon"></i>
									<span className="dashboard-widget-title-text">Schnellaktionen</span>
								</h5>
								<div className="d-grid gap-2">
									<button
										className="btn btn-primary"
										onClick={() => setExpenseEntryMask(true)}
									>
										<i className="bi bi-clock-history me-2"></i>
										Aufwand buchen
									</button>
									<Link to="/calendar" className="btn btn-outline-primary">
										<i className="bi bi-calendar3 me-2"></i>
										Zum Slot-Kalender
									</Link>
									<Link to="/team" className="btn btn-outline-primary">
										<i className="bi bi-people me-2"></i>
										Team-Übersicht
									</Link>
								</div>
							</div>
						</div>
					</div>

					<div className="col-md-4">
						<div className="card h-100 border-0 dashboard-widget-card">
							<div className="dashboard-widget-gradient dashboard-widget-gradient--danger"></div>
							<div className="card-body position-relative dashboard-widget-body">
								<h5 className="card-title mb-3 dashboard-widget-title">
									<i className="bi bi-bar-chart me-2 text-primary dashboard-widget-title-icon"></i>
									<span className="dashboard-widget-title-text">Meine Woche</span>
								</h5>
								{loadingStats ? (
									<div className="text-center py-3">
										<div className="spinner-border text-primary" role="status"></div>
									</div>
								) : (
									<>
										<div className="mb-2 dashboard-time-stats">
											<div className="d-flex justify-content-between align-items-center">
												<div>
													<div className="text-muted small mb-1 dashboard-stats-label">GEBUCHTE ZEIT</div>
													<div className="fs-4 fw-bold text-primary mb-0">{formatMinutes(totalWeeklyMinutes)}</div>
												</div>
												<div className="dashboard-stats-icon-box">
													<i className="bi bi-clock-fill text-primary dashboard-stats-icon"></i>
												</div>
											</div>
										</div>

										<div className="mb-2 dashboard-projects-box">
											<div className="d-flex justify-content-between align-items-center">
												<span className="text-muted small dashboard-stats-label">
													PROJEKTE
												</span>
												<span className="fs-5 fw-bold dashboard-projects-count">{weeklyExpenses.length}</span>
											</div>
										</div>

										<div className="mb-2 dashboard-streak-box">
											<div className="d-flex justify-content-between align-items-center mb-2">
												<span className="text-muted small dashboard-stats-label">WOCHENSTREAK</span>
												<span className="badge dashboard-streak-badge">{streak} / 5</span>
											</div>
											<div className="d-flex justify-content-center align-items-center gap-2">
												{[1, 2, 3, 4, 5].map((day) => (
													<div
														key={day}
														className={`dashboard-streak-icon-container ${day <= streak ? 'dashboard-streak-icon-container--active' : ''}`}
													>
														<i
															className={`bi bi-fire dashboard-streak-icon ${day <= streak ? 'text-danger dashboard-streak-icon--active' : 'text-muted dashboard-streak-icon--inactive'}`}
														></i>
													</div>
												))}
											</div>
										</div>

										<Link to="/expenses" className="btn btn-sm btn-outline-primary mt-2 w-100">
											<i className="bi bi-list-ul me-2"></i>
											Aufwände anzeigen
										</Link>
									</>
								)}
							</div>
						</div>
					</div>

					<div className="col-md-4">
						<div className="card h-100 border-0 dashboard-widget-card">
							<div className="dashboard-widget-gradient dashboard-widget-gradient--success"></div>
							<div className="card-body position-relative dashboard-widget-body">
								<h5 className="card-title mb-3 dashboard-widget-title">
									<i className="bi bi-calendar-week me-2 text-success dashboard-widget-title-icon"></i>
									<span className="dashboard-widget-title-text">Kalenderwoche</span>
								</h5>
								<div className="dashboard-calendar-week-box">
									<div className="dashboard-calendar-week-gradient"></div>
									<div className="position-relative">
										<div className="dashboard-calendar-week-number">
											{getCalendarWeekForDate(new Date())}
										</div>
										<div className="d-flex align-items-center justify-content-center gap-2 text-muted dashboard-calendar-week-dates">
											<i className="bi bi-calendar-range dashboard-calendar-week-icon"></i>
											<span>{moment(getMonday(new Date())).format("DD.MM.")} - {moment(getFriday(new Date())).format("DD.MM.YYYY")}</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* All Users Daily Stats Widget - only show in global mode */}
				{showGlobalStats && (
					<div className="row g-3 mb-4">
						<div className="col-md-12">
							<AllUsersDailyStatsWidget />
						</div>
					</div>
				)}

				{/* Monthly Stats Widget - only show in global mode */}
				{showGlobalStats && (
					<div className="row g-3 mb-4">
						<div className="col-md-12">
							<MonthlyStatsWidget />
						</div>
					</div>
				)}

				{/* 12-Week Stats Widget - only show in personal mode */}
				{!showGlobalStats && (
					<div className="row g-3 mb-4">
						<div className="col-md-12">
							<WeeklyHoursWidget />
						</div>
					</div>
				)}

				{/* Activity Feed */}
				<div className="row g-3 mb-4">
					<div className="col-md-12">
						<div className="card dashboard-activity-card">
							<div className="card-header border-bottom d-flex justify-content-between align-items-center dashboard-activity-header">
								<h5 className="mb-0">
									<i className="bi bi-activity me-2 text-primary"></i>
									Meine letzten Aktivitäten
								</h5>
								<Link to="/profile#activity" className="btn btn-sm btn-outline-primary">
									Alle anzeigen
								</Link>
							</div>
							<div className="card-body">
								<ActivityFeed limit={5} compact={true} />
							</div>
						</div>
					</div>
				</div>

				{/* Quick Links & Release Notes */}
				<div className="row g-3 mb-4">
					<div className="col-md-6">
						<div className="card shadow-sm h-100">
							<div className="card-header bg-white border-bottom">
								<h5 className="mb-0">
									<i className="bi bi-link-45deg me-2 text-primary"></i>
									Quick Links
								</h5>
							</div>
							<div className="card-body">
								<div className="list-group list-group-flush">
									<a
										href="https://unternehmenonlinedo.sharepoint.com/UO-Intranet/Lists/Urlaubskalender/calendar.aspx"
										target="_blank"
										rel="noopener noreferrer"
										className="list-group-item list-group-item-action border-0 px-0 py-2"
									>
										<i className="bi bi-calendar-check me-2 text-primary"></i>
										Urlaubskalender
										<i className="bi bi-box-arrow-up-right ms-2 text-muted small"></i>
									</a>
									<a
										href="https://wiki.shared-01.uo-cloud.de/books/arbeiten-bei-uo/page/vpn-zugang-uo"
										target="_blank"
										rel="noopener noreferrer"
										className="list-group-item list-group-item-action border-0 px-0 py-2"
									>
										<i className="bi bi-shield-lock me-2 text-primary"></i>
										VPN-Zugang
										<i className="bi bi-box-arrow-up-right ms-2 text-muted small"></i>
									</a>
									<a
										href="https://wiki.shared-01.uo-cloud.de/books/arbeiten-bei-uo/page/lizenzen-bei-unternehmenonline"
										target="_blank"
										rel="noopener noreferrer"
										className="list-group-item list-group-item-action border-0 px-0 py-2"
									>
										<i className="bi bi-key me-2 text-primary"></i>
										Software-Lizenzen
										<i className="bi bi-box-arrow-up-right ms-2 text-muted small"></i>
									</a>
									<a
										href="https://wiki.shared-01.uo-cloud.de/books/arbeiten-bei-uo/chapter/weclapp"
										target="_blank"
										rel="noopener noreferrer"
										className="list-group-item list-group-item-action border-0 px-0 py-2"
									>
										<i className="bi bi-box me-2 text-primary"></i>
										WeClapp (ERP)
										<i className="bi bi-box-arrow-up-right ms-2 text-muted small"></i>
									</a>
								</div>
							</div>
						</div>
					</div>

					<div className="col-md-6">
						<div className="card shadow-sm h-100">
							<div className="card-header bg-white border-bottom">
								<h5 className="mb-0">
									<i className="bi bi-megaphone me-2 text-primary"></i>
									Neuigkeiten <span className="badge bg-primary ms-2">v{SyncConstants["slotifyVersion"]}</span>
								</h5>
							</div>
							<div className="card-body">
								<ul className="list-unstyled mb-0">
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Unterstützung für mehrere Kostentypen.</strong> Slotify unterstützt nun mehrere Kostentypen (neben Arbeitsaufwänden), als Vorbereitung für die Domainabrechnung. Aktuell werden einfache, externe Kosten als Projekttyp unterstützt, die auch zu WeClapp übertragen werden können.
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Support für Non-Prod APIs.</strong> Beim Login kann optional ein anderer Slotify-API-Server gewählt werden (etwa für die lokale Entwicklung).
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Die dritte Null (:000) ist nun entfernt.</strong> Falsches TYpecasting führte zu einer verwirrenden Anzeige.
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Komfort-Features zur Abrechnung.</strong> Kleine Verbesserungen des Abrechnungs-Workflows.
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Schnelleingabemaske.</strong> Die neue Schnelleingabemaske ist jetzt verfügbar.
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Verbesserte Entry-List-Ansicht.</strong> Kleinere Anpassungen.
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Menü-Anpassungen.</strong> Kleinere Anpassungen am Menü.
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Globale Statistiken.</strong> Auch Nicht-Administratoren sehen nun globale Statistiken.
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Admin: Team-Aufwände Widget.</strong> Administratoren sehen im Dashboard ein neues Dark-Mode Widget mit den Gesamtaufwänden aller Benutzer der letzten 10 Tage als Balkendiagramm.
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Trend-Anzeige für Aufwände.</strong> Das Dashboard zeigt nun einen Trend für die gebuchten Aufwände an.
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>16-Wochen Aktivitäts-Widget.</strong> Neues Dashboard-Widget zeigt deine gebuchten Stunden der letzten 16 Wochen als Statistik sowie eine Aktivitäts-Matrix für alle Zeiträume.
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Drag & Drop im Slot-Kalender.</strong> Slots können jetzt per Drag & Drop verschoben werden - einfach ziehen und auf einem anderen Feld ablegen.
									</li>
									<li className="mb-2">
										<i className="bi bi-stars me-2 text-warning"></i>
										<strong>Kontextmenü für Slots.</strong> Rechtsklick auf einen Slot öffnet ein modernes Kontextmenü mit Schnellaktionen.
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Dashboard;
